import { html, css, svg, TemplateResult, CSSResultGroup } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { BaseComponent } from './base-component.js'
import { animate } from '@lit-labs/motion'
import { animateWidthContainer } from './motion/animations.js'

@customElement('il-plus-minus-input')
export default class PlusMinusInputComponent extends BaseComponent {
  @property({ type: Boolean }) disabled: boolean = false
  @property({ type: Number }) count: number = 0
  @property({ type: Number }) max: number = 0
  @property({ type: Number }) min: number = 0

  /** Keeps the minus button and count visible at zero instead of collapsing them away. */
  @property({ type: Boolean, attribute: 'always-show-count' }) alwaysShowCount: boolean = false

  @property({ type: String, attribute: 'plus-tooltip' }) plusTooltip: string = ''
  @property({ type: String, attribute: 'minus-tooltip' }) minusTooltip: string = ''

  @property({ type: Object }) countIncremented?: () => void
  @property({ type: Object }) countDecremented?: () => void

  private get _canIncrement(): boolean {
    return !this.disabled && this.count < this.max
  }

  private get _canDecrement(): boolean {
    return !this.disabled && this.count > this.min
  }

  private get _disablePlusTooltip(): boolean {
    return !this.plusTooltip
  }

  private get _disableMinusTooltip(): boolean {
    return !this.minusTooltip
  }

  override render(): TemplateResult {
    return html`
      <div class="il-plus-minus-input ${this.count > 0 || this.alwaysShowCount ? 'il-plus-minus-input--with-count' : ''}" data-testid="iliad-plus-minus-input">
        ${this._renderCountContainer()}

        <il-tooltip tooltip-text="${this.plusTooltip}" ?disable-content-hovering=${this._disablePlusTooltip}>
          <il-button
            variant="secondary"
            small
            icon-only
            color="primary"
            ?disabled=${!this._canIncrement}
            .onClick=${() => this.countIncremented?.()}
            data-testid="iliad-plus-minus-input-button-plus"
          >
            ${this._plusIcon}
          </il-button>
        </il-tooltip>
      </div>
    `
  }

  private _renderCountContainer(): TemplateResult {
    return html`
      <div class="il-plus-minus-input__count-container" ${animate(animateWidthContainer({ fast: true }))}>
        <il-tooltip tooltip-text="${this.minusTooltip}" ?disable-content-hovering=${this._disableMinusTooltip}>
          <il-button
            small
            icon-only
            color="primary"
            variant="secondary"
            ?disabled=${!this._canDecrement}
            .onClick=${() => this.countDecremented?.()}
            data-testid="iliad-plus-minus-input-button-minus"
          >
            ${this._minusIcon}
          </il-button>
        </il-tooltip>

        <span class="il-plus-minus-input__count" data-testid="iliad-plus-minus-input-count"> ${this.count} </span>
      </div>
    `
  }

  private _plusIcon: TemplateResult = svg`
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.12499 15.833V10.875H4.16699V9.12499H9.12499V4.16699H10.875V9.12499H15.833V10.875H10.875V15.833H9.12499Z" />
      </svg>
    `

  private _minusIcon: TemplateResult = svg`
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M4.16699 10.833V9.08301H15.833V10.833H4.16699Z" />
      </svg>
    `

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
    .il-plus-minus-input {
      display: flex;
      gap: 8px;
    }

    .il-plus-minus-input__count-container {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      width: 0;
      opacity: 0;
      overflow: hidden;

      transition: opacity 0.2s ease;
    }

    .il-plus-minus-input--with-count .il-plus-minus-input__count-container {
      width: auto;
      opacity: 1;
    }

    .il-plus-minus-input__count {
      font-size: var(--il-font-size-16);
      font-weight: var(--il-font-weight-xbold);
      line-height: var(--il-font-line-height-125);
      letter-spacing: var(--il-font-letter-spacing-2);
      color: var(--il-color-neutrals-350);

      width: 20px;
      text-align: center;
    }
  `]
}
